import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssignmentDetail,
  AssignmentView,
  Contractor,
  CreateMixInput,
  MixInfo,
  CreateAssignmentInput,
  CreateContractorInput,
  CreateProjectInput,
  DownloadResponse,
  NotificationsResponse,
  Project,
  ProjectDetail,
  ProjectSummary,
  ReviewInput,
  StatusBoard,
  TranslateResponse,
  UiLocale,
  UpdateAssignmentInput,
  UpdateContractorInput,
  UpdateProjectInput,
} from '@dubforge/shared';
import { api } from './client';

export const keys = {
  contractors: ['contractors'] as const,
  projects: ['projects'] as const,
  project: (id: string) => ['projects', id] as const,
  assignments: ['assignments'] as const,
  assignment: (id: string) => ['assignments', id] as const,
  statusBoard: ['status-board'] as const,
  notifications: ['notifications'] as const,
};

/** Most changes ripple into several views (board, project, payments), so refresh them together. */
function useInvalidateWork() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: keys.projects }),
      qc.invalidateQueries({ queryKey: keys.assignments }),
      qc.invalidateQueries({ queryKey: keys.statusBoard }),
    ]);
}

// ---------- Contractors ----------
export const useContractors = () =>
  useQuery({ queryKey: keys.contractors, queryFn: () => api<Contractor[]>('GET', '/contractors') });

export function useCreateContractor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContractorInput) => api<Contractor>('POST', '/contractors', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.contractors }),
  });
}

export function useUpdateContractor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateContractorInput & { id: string }) =>
      api<Contractor>('PATCH', `/contractors/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.contractors }),
  });
}

export function useTestEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api('POST', `/contractors/${id}/test-email`),
    // The status updates once the email has been tried; look again shortly.
    onSuccess: () => setTimeout(() => void qc.invalidateQueries({ queryKey: keys.contractors }), 8000),
  });
}

// ---------- Projects ----------
export const useProjects = () =>
  useQuery({ queryKey: keys.projects, queryFn: () => api<ProjectSummary[]>('GET', '/projects') });

export const useProject = (id: string) =>
  useQuery({
    queryKey: keys.project(id),
    queryFn: () => api<ProjectDetail>('GET', `/projects/${id}`),
    // Check back while the video is being processed or the package built.
    refetchInterval: (query) => {
      const p = query.state.data;
      const busy = p?.video?.status === 'processing' || p?.package?.status === 'building';
      return busy ? 5000 : false;
    },
  });

export function useRebuildPackage(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<Project>('POST', `/projects/${projectId}/package/rebuild`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.project(projectId) }),
  });
}

export function useCreateProject() {
  const invalidate = useInvalidateWork();
  return useMutation({
    mutationFn: (input: CreateProjectInput) => api<Project>('POST', '/projects', input),
    onSuccess: invalidate,
  });
}

export function useUpdateProject(id: string) {
  const invalidate = useInvalidateWork();
  return useMutation({
    mutationFn: (input: UpdateProjectInput) => api<Project>('PATCH', `/projects/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteAsset(projectId: string) {
  const invalidate = useInvalidateWork();
  return useMutation({
    mutationFn: (assetId: string) => api('DELETE', `/projects/${projectId}/assets/${assetId}`),
    onSuccess: invalidate,
  });
}

export function useCreateAssignment(projectId: string) {
  const invalidate = useInvalidateWork();
  return useMutation({
    mutationFn: (input: CreateAssignmentInput) =>
      api<AssignmentView>('POST', `/projects/${projectId}/assignments`, input),
    onSuccess: invalidate,
  });
}

// ---------- Assignments ----------
export const useAssignments = () =>
  useQuery({ queryKey: keys.assignments, queryFn: () => api<AssignmentView[]>('GET', '/assignments') });

export const useAssignment = (id: string) =>
  useQuery({
    queryKey: keys.assignment(id),
    queryFn: () => api<AssignmentDetail>('GET', `/assignments/${id}`),
    // Check back every few seconds while a mix is being made.
    refetchInterval: (query) =>
      query.state.data?.submissions.some((s) => s.mix?.status === 'processing') ? 4000 : false,
  });

export function useUpdateAssignment() {
  const invalidate = useInvalidateWork();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateAssignmentInput & { id: string }) =>
      api<AssignmentView>('PATCH', `/assignments/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteAssignment() {
  const invalidate = useInvalidateWork();
  return useMutation({
    mutationFn: (id: string) => api('DELETE', `/assignments/${id}`),
    onSuccess: invalidate,
  });
}

export function useReview(assignmentId: string) {
  const invalidate = useInvalidateWork();
  return useMutation({
    mutationFn: ({ version, ...input }: ReviewInput & { version: number }) =>
      api<AssignmentView>('POST', `/assignments/${assignmentId}/submissions/${version}/review`, input),
    onSuccess: invalidate,
  });
}

export const getAssetDownload = (
  assignmentId: string | null,
  projectId: string,
  assetId: string,
  inline = false,
) =>
  api<DownloadResponse>(
    'GET',
    (assignmentId
      ? `/assignments/${assignmentId}/assets/${assetId}/download`
      : `/projects/${projectId}/assets/${assetId}/download`) + (inline ? '?inline=1' : ''),
  );

export function useCreateMix(assignmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ version, ...input }: CreateMixInput & { version: number }) =>
      api<MixInfo>('POST', `/assignments/${assignmentId}/submissions/${version}/mix`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.assignment(assignmentId) }),
  });
}

export const getMixUrl = (assignmentId: string, version: number, format: 'wav' | 'mp3', inline = false) =>
  api<DownloadResponse>(
    'GET',
    `/assignments/${assignmentId}/submissions/${version}/mix/download?format=${format}${inline ? '&inline=1' : ''}`,
  );

export const getSubmissionUrl = (assignmentId: string, version: number, inline: boolean) =>
  api<DownloadResponse>(
    'GET',
    `/assignments/${assignmentId}/submissions/${version}/download${inline ? '?inline=1' : ''}`,
  );

// ---------- Board & notifications ----------
export const useStatusBoard = () =>
  useQuery({ queryKey: keys.statusBoard, queryFn: () => api<StatusBoard>('GET', '/status-board') });

export const useNotifications = () =>
  useQuery({
    queryKey: keys.notifications,
    queryFn: () => api<NotificationsResponse>('GET', '/notifications'),
    refetchInterval: 60_000,
  });

export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids?: string[]; all?: boolean }) => api('POST', '/notifications/read', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.notifications }),
  });
}

/** Opens a presigned download URL; the browser saves the file because S3 sends it as an attachment. */
export function startDownload(url: string) {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// ---------- Language ----------
export const saveLocale = (locale: UiLocale) => api('PUT', '/me/settings', { locale });
export const saveEmail = (email: string) => api('PUT', '/me/settings', { email });

/** Machine translation of a note written in another language. Cached for the session. */
export const useMachineTranslation = (text: string, source: UiLocale, target: UiLocale, enabled: boolean) =>
  useQuery({
    queryKey: ['translate', source, target, text],
    queryFn: async () =>
      (await api<TranslateResponse>('POST', '/translate', { texts: [text], source, target })).translations[0] ?? text,
    enabled: enabled && !!text.trim() && source !== target,
    staleTime: Infinity,
    gcTime: 60 * 60 * 1000,
  });
